// 配置できる種類ごとに「最後に配置した・手でリサイズしたサイズ」と「前回置いた種別」を
// localStorage（`refinement-canvas:placement-size:v1`）に記憶する。ツールバーからのクリック配置・
// 空白部のダブルクリック・右クリックメニューの「図形を配置」が、この記憶から同じ種類のサイズを使う。
//
// - 作品データ（キャンバス）ではなく、利用者のブラウザーごとの設定として持つ。サーバーへは送らない。
// - 寸法はステージ座標（world px）で持ち、拡大率は持たない。
// - 記憶の単位・制約は object-placement-defaults.ts が正本で、ここでは検証と保存だけを行う。
// - 前回の種別だけを持つ旧キー（last-placed-object-preference.ts）は削除せず、互換のため読み書きを続ける。
//   新しい記憶に種別が無い（初回・破損）ときは旧キーの種別を引き継ぐ。
// - 読み書きの例外では、このタブのメモリ上の記憶で配置を続ける（配置そのものは止めない）。
//
// 楽観保存とロールバック:
// 配置・リサイズの op を dispatcher がローカルで受理した時点でサイズを保存し、記録（revert / settle）を返す。
// op がサーバーに拒否されたら revert を呼ぶ。その記録が種類ごとの最新のときだけ直前の値へ戻し、
// 後から別の操作で上書きされていれば値は戻さない（記録の連なりから外すだけ）。op が確定したら settle を
// 呼び、その記録とそれより前の記録を連なりから外す（確定した値より前の記録の拒否は、もう値を変えない）。
// 連なりは未確定の記録だけを持ち、件数の上限で記録を捨てることはしない。
//
// 他タブとの整合:
// 他タブの保存は storage イベントに加え、読み込み・保存・ロールバックの直前にも保存値を突き合わせて
// 取り込む（イベントの処理より先に拒否や保存が来ても、他タブの新しい値を古い値で上書きしない）。
// 値か書き込み ID が変わった種類は他タブの値を最新とし、その種類の未確定の記録を捨てる。JSON として
// 読めない・version が違う保存値は取り込まない（このタブの記憶を保ち、次の保存で正しい形式へ戻す）。
// 書き込み ID は、サイズを書き込むたびに種類ごとに振り直す値で、同じ寸法の書き込みでも、他タブが
// 書き直した記録と、このタブの未確定の記録を見分けるために保存する（寸法が同じでも ID が違えば他タブの
// 書き直しとし、ID の無い保存値も、自分の記録かどうかを見分けられないため同じく扱う）。

import type { CanvasObject, ShapeCanvasObject } from "@refinement-canvas/domain";
import { generateCanvasOpId } from "./canvas-op-id";
import {
  PLACEMENT_SIZE_KEYS,
  clampPlacementSize,
  placementSizeKeyOfObject,
  placementSizeLimitsOf,
  type PlacementSizeKey,
  type RememberedPlacementSize,
} from "../features/canvas/object-placement-defaults";
import {
  DEFAULT_PLACED_OBJECT_KIND,
  isPlaceableObjectKind,
  readStoredLastPlacedObjectKind,
  saveLastPlacedObjectKind,
  type PlaceableObjectKind,
} from "./last-placed-object-preference";

const KEY = "refinement-canvas:placement-size:v1";
const VERSION = 1;
/** これより長い保存値は読まない（巨大な値の解析で配置を遅らせないための上限）。 */
const RAW_MAX_LENGTH = 16 * 1024;
/** これより長い書き込み ID は読まない（このモジュールが振る ID は 16 文字）。 */
const WRITE_ID_MAX_LENGTH = 64;

type ShapeKind = ShapeCanvasObject["shapeKind"];
type RememberedSizes = Readonly<Partial<Record<PlacementSizeKey, RememberedPlacementSize>>>;
/** 種類ごとの書き込み ID（サイズを書き込むたびに振り直す）。 */
type SizeWriteIds = Readonly<Partial<Record<PlacementSizeKey, string>>>;

/** 配置に使う記憶の内容。 */
export interface PlacementPreference {
  /** 前回置いた種別。記録が無ければ既定（テキスト）。 */
  readonly lastPlacedKind: PlaceableObjectKind;
  /** 前回置いた図形の種類。未記録なら undefined（呼び出し側は現在の選択へ戻す）。 */
  readonly lastShapeKind: ShapeKind | undefined;
  readonly sizes: RememberedSizes;
}

/**
 * サイズの記録 1 件。op がサーバーに拒否されたら revert を、確定したら settle を呼ぶ。
 * どちらか一方の最初の 1 回だけが効き、以後の呼び出しは何もしない。
 */
export interface PlacementSizeRecord {
  readonly revert: () => void;
  readonly settle: () => void;
}

interface ParsedPreference {
  readonly lastPlacedKind: PlaceableObjectKind | undefined;
  readonly lastShapeKind: ShapeKind | undefined;
  readonly sizes: RememberedSizes;
  readonly sizeWriteIds: SizeWriteIds;
}

interface MemoryState {
  lastPlacedKind: PlaceableObjectKind | undefined;
  lastShapeKind: ShapeKind | undefined;
  sizes: RememberedSizes;
  sizeWriteIds: SizeWriteIds;
}

/**
 * 未確定の記録。previous・previousWriteId は、この記録より前の値と書き込み ID
 * （連なりから外すときに次の記録へ引き継ぐ）。
 */
interface PendingWrite {
  readonly key: PlacementSizeKey;
  previous: RememberedPlacementSize | undefined;
  previousWriteId: string | undefined;
}

const EMPTY_PARSED: ParsedPreference = {
  lastPlacedKind: undefined,
  lastShapeKind: undefined,
  sizes: {},
  sizeWriteIds: {},
};

const NOOP_RECORD: PlacementSizeRecord = { revert: () => {}, settle: () => {} };

let memory: MemoryState | null = null;
const pendingWrites = new Map<PlacementSizeKey, PendingWrite[]>();
/**
 * このタブが最後にストレージと突き合わせた保存値（読み込み・保存・取り込みの時点）。
 * これと異なる保存値は、他タブ（または旧クライアント・手作業）による変更とみなす。undefined は未取得。
 */
let lastSyncedRaw: string | null | undefined;
let storageListenerInstalled = false;

/** 記憶を読む。初回はストレージから読み込み、以後はこのタブのメモリ上の記憶（他タブの保存を反映済み）を返す。 */
export function loadPlacementPreference(): PlacementPreference {
  const state = ensureMemory();
  syncExternalChanges(state);
  return {
    lastPlacedKind: state.lastPlacedKind ?? DEFAULT_PLACED_OBJECT_KIND,
    lastShapeKind: state.lastShapeKind,
    sizes: state.sizes,
  };
}

/**
 * 配置した種別（図形は種類も）を記録する。size を渡したときは、その種類のサイズの記憶も更新する。
 * 戻り値の revert は配置の op が拒否されたときに呼び、サイズの記憶だけを戻す
 * （前回の種別は従来どおり「操作の履歴」として戻さない）。settle は op が確定したときに呼ぶ。
 */
export function recordPlacement(params: {
  readonly kind: PlaceableObjectKind;
  readonly shapeKind?: ShapeKind | undefined;
  readonly size?:
    | { readonly key: PlacementSizeKey; readonly value: RememberedPlacementSize }
    | undefined;
}): PlacementSizeRecord {
  const state = ensureMemory();
  syncExternalChanges(state);
  state.lastPlacedKind = params.kind;
  if (params.kind === "shape" && params.shapeKind !== undefined) {
    state.lastShapeKind = params.shapeKind;
  }
  const record =
    params.size === undefined ? NOOP_RECORD : writeSize(state, params.size.key, params.size.value);
  persist(state);
  // 旧クライアントと、新しい記憶が壊れたときの拠り所として、旧キーの種別も更新する。
  saveLastPlacedObjectKind(params.kind);
  return record;
}

/**
 * リサイズハンドルで確定した大きさを、そのオブジェクトの種類のサイズとして記憶する。
 * 前回の種別は変えない（リサイズは配置ではないため）。
 * テキストの幅だけを変えた場合は高さを記憶し直さない。本文に合わせて伸びた高さは利用者が
 * 選んだ高さではないため、記憶済みの高さ（無ければ高さなし）を保つ。
 */
export function recordResizedObjectSize(
  object: CanvasObject,
  frame: { readonly w: number; readonly h: number },
  options: { readonly heightChanged: boolean }
): PlacementSizeRecord {
  const key = placementSizeKeyOfObject(object);
  if (key === null) return NOOP_RECORD;
  const state = ensureMemory();
  syncExternalChanges(state);
  let value: RememberedPlacementSize;
  if (key === "text" && !options.heightChanged) {
    const rememberedHeight = state.sizes.text?.h;
    value = rememberedHeight === undefined ? { w: frame.w } : { w: frame.w, h: rememberedHeight };
  } else {
    value = { w: frame.w, h: frame.h };
  }
  const record = writeSize(state, key, value);
  persist(state);
  return record;
}

/** op の結末（拒否・確定）を、op を送った後に作るサイズの記録へ結び付ける窓口。 */
export interface PlacementSizeOutcome {
  /** dispatcher へ渡す拒否の通知先。 */
  readonly onRejected: () => void;
  /** dispatcher へ渡す確定の通知先。 */
  readonly onSettled: () => void;
  /** 記録を作る前に拒否が届いたか。届いていれば、その op では記録を作らない。 */
  readonly isRejected: () => boolean;
  /** 作った記録を結び付ける。記録を作る前に結末が届いていれば、その場で反映する。 */
  readonly bind: (record: PlacementSizeRecord) => void;
}

/**
 * op の結末と記録を結び付ける窓口を作る。送信サイズ上限の超過（送信の中で同期的に拒否）や、送信の中で
 * 確定した場合のように、記録を作る前に結末が届いても取りこぼさない。拒否と確定は先に届いた方だけを使う。
 */
export function createPlacementSizeOutcome(): PlacementSizeOutcome {
  let record: PlacementSizeRecord | null = null;
  let outcome: "pending" | "rejected" | "settled" = "pending";
  const apply = (target: PlacementSizeRecord): void => {
    if (outcome === "rejected") target.revert();
    else if (outcome === "settled") target.settle();
  };
  return {
    onRejected: () => {
      if (outcome !== "pending") return;
      outcome = "rejected";
      if (record !== null) apply(record);
    },
    onSettled: () => {
      if (outcome !== "pending") return;
      outcome = "settled";
      if (record !== null) apply(record);
    },
    isRejected: () => outcome === "rejected",
    bind: (next) => {
      record = next;
      apply(next);
    },
  };
}

/** テスト用: メモリ上の記憶と未確定の記録を捨て、次の読み込みでストレージから読み直させる。 */
export function resetPlacementSizePreferenceForTest(): void {
  memory = null;
  pendingWrites.clear();
  lastSyncedRaw = undefined;
}

/** テスト用: 連なりに残っている未確定の記録の件数（確定・拒否・他タブの取り込みで外れたものは数えない）。 */
export function countPendingPlacementSizeWritesForTest(): number {
  let count = 0;
  for (const chain of pendingWrites.values()) count += chain.length;
  return count;
}

function ensureMemory(): MemoryState {
  if (memory !== null) return memory;
  const raw = readRaw();
  lastSyncedRaw = raw;
  const parsed = raw === undefined ? EMPTY_PARSED : parsePlacementPreference(raw);
  memory = {
    // 新しい記憶に種別が無い（初回・破損）ときは旧キーの種別を引き継ぐ。
    lastPlacedKind: parsed.lastPlacedKind ?? readStoredLastPlacedObjectKind() ?? undefined,
    lastShapeKind: parsed.lastShapeKind,
    sizes: parsed.sizes,
    sizeWriteIds: parsed.sizeWriteIds,
  };
  installStorageListener();
  return memory;
}

/** ストレージの値を読む。読めない環境では undefined（null は「保存値が無い」）。 */
function readRaw(): string | null | undefined {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return undefined;
  }
}

/**
 * 前回の突き合わせ以降に他タブが保存した値を取り込む。storage イベントの処理を待たずに、読み込み・
 * 保存・ロールバックの直前に呼ぶ（このタブの古い値で他タブの新しい値を上書きしないため）。
 */
function syncExternalChanges(state: MemoryState): void {
  const raw = readRaw();
  // 読めない環境、または前回突き合わせた値から変わっていない（他タブの保存が無い）ときは何もしない。
  if (raw === undefined || raw === lastSyncedRaw) return;
  lastSyncedRaw = raw;
  const parsed = parseStoredPreference(raw);
  // 壊れた保存値・別 version は取り込まない（このタブの記憶を保ち、次の保存で正しい形式へ戻す）。
  if (parsed === null) return;
  importExternalPreference(state, parsed);
}

function persist(state: MemoryState): void {
  const sizes: Partial<Record<PlacementSizeKey, RememberedPlacementSize>> = {};
  const sizeWriteIds: Partial<Record<PlacementSizeKey, string>> = {};
  for (const key of PLACEMENT_SIZE_KEYS) {
    const entry = state.sizes[key];
    if (entry === undefined) continue;
    sizes[key] = entry;
    const writeId = state.sizeWriteIds[key];
    if (writeId !== undefined) sizeWriteIds[key] = writeId;
  }
  const payload = {
    version: VERSION,
    ...(state.lastPlacedKind !== undefined ? { lastPlacedKind: state.lastPlacedKind } : {}),
    ...(state.lastShapeKind !== undefined ? { lastShapeKind: state.lastShapeKind } : {}),
    sizes,
    ...(Object.keys(sizeWriteIds).length > 0 ? { sizeWriteIds } : {}),
  };
  const raw = JSON.stringify(payload);
  try {
    window.localStorage.setItem(KEY, raw);
    lastSyncedRaw = raw;
  } catch {
    // 保存できない環境でも、このタブではメモリ上の記憶で配置を続ける。
  }
}

function writeSize(
  state: MemoryState,
  key: PlacementSizeKey,
  value: RememberedPlacementSize
): PlacementSizeRecord {
  const normalized = normalizeRememberedSize(key, value);
  if (normalized === null) return NOOP_RECORD;
  const entry: PendingWrite = {
    key,
    previous: state.sizes[key],
    previousWriteId: state.sizeWriteIds[key],
  };
  state.sizes = withSize(state.sizes, key, normalized);
  // 同じ寸法の書き込みでも、他タブがこの書き込みを見分けられるよう、書き込みごとに ID を振り直す。
  state.sizeWriteIds = withSizeWriteId(state.sizeWriteIds, key, generateCanvasOpId());
  const chain = pendingWrites.get(key) ?? [];
  chain.push(entry);
  pendingWrites.set(key, chain);
  let done = false;
  return {
    revert: () => {
      if (done) return;
      done = true;
      revertWrite(entry);
    },
    settle: () => {
      if (done) return;
      done = true;
      settleWrite(entry);
    },
  };
}

function revertWrite(entry: PendingWrite): void {
  const state = memory;
  if (state === null) return;
  // 他タブの保存を先に取り込む。この種類が他タブで変わっていれば連なりごと捨てられ、値は戻さない。
  syncExternalChanges(state);
  const chain = pendingWrites.get(entry.key);
  if (chain === undefined) return;
  const index = chain.indexOf(entry);
  // 他タブの値を取り込んだ・後の記録が確定した記録は、もう値を戻さない。
  if (index < 0) return;
  if (index === chain.length - 1) {
    // この記録が最新なので、記録より前の値（と書き込み ID）へ戻す。
    chain.pop();
    state.sizes = withSize(state.sizes, entry.key, entry.previous);
    state.sizeWriteIds = withSizeWriteId(state.sizeWriteIds, entry.key, entry.previousWriteId);
    persist(state);
  } else {
    // 後から別の操作で上書きされている。値は戻さず、次の記録が戻す先をこの記録より前の値にする。
    const next = chain[index + 1];
    if (next !== undefined) {
      next.previous = entry.previous;
      next.previousWriteId = entry.previousWriteId;
    }
    chain.splice(index, 1);
  }
  if (chain.length === 0) pendingWrites.delete(entry.key);
}

/**
 * 確定した記録と、それより前の記録を連なりから外す。確定した値より前の記録が後から拒否されても、
 * 記憶の値は（確定した値かそれより新しい値のままで）変わらないため。
 */
function settleWrite(entry: PendingWrite): void {
  const chain = pendingWrites.get(entry.key);
  if (chain === undefined) return;
  const index = chain.indexOf(entry);
  if (index < 0) return;
  chain.splice(0, index + 1);
  if (chain.length === 0) pendingWrites.delete(entry.key);
}

/** 保存する前に種類ごとの制約へ丸める。テキスト以外で高さが無い値は記録しない。 */
function normalizeRememberedSize(
  key: PlacementSizeKey,
  value: RememberedPlacementSize
): RememberedPlacementSize | null {
  if (value.h === undefined) {
    if (key !== "text") return null;
    const limits = placementSizeLimitsOf(key);
    return { w: clampPlacementSize(key, { w: value.w, h: limits.minH }).w };
  }
  return clampPlacementSize(key, { w: value.w, h: value.h });
}

function withSize(
  sizes: RememberedSizes,
  key: PlacementSizeKey,
  value: RememberedPlacementSize | undefined
): RememberedSizes {
  const next: Partial<Record<PlacementSizeKey, RememberedPlacementSize>> = { ...sizes };
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

function withSizeWriteId(
  ids: SizeWriteIds,
  key: PlacementSizeKey,
  value: string | undefined
): SizeWriteIds {
  const next: Partial<Record<PlacementSizeKey, string>> = { ...ids };
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

/**
 * 保存値を解析する。JSON として読めない・version が違う・巨大な値は丸ごと無視し（既定へ戻す）、
 * 種類ごとのサイズは有限・正・その種類の上限以内のものだけを受け付けて、無効なものだけを捨てる。
 */
export function parsePlacementPreference(raw: string | null): ParsedPreference {
  return parseStoredPreference(raw) ?? EMPTY_PARSED;
}

/**
 * 保存値を解析する。保存値が無い（null）ときは空の記憶、JSON として読めない・version が違う・
 * 巨大な値のときは null（有効な保存値ではない）を返す。
 */
function parseStoredPreference(raw: string | null): ParsedPreference | null {
  if (raw === null) return EMPTY_PARSED;
  if (raw.length > RAW_MAX_LENGTH) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || value.version !== VERSION) return null;
  const source = isRecord(value.sizes) ? value.sizes : {};
  const writeIdSource = isRecord(value.sizeWriteIds) ? value.sizeWriteIds : {};
  const sizes: Partial<Record<PlacementSizeKey, RememberedPlacementSize>> = {};
  const sizeWriteIds: Partial<Record<PlacementSizeKey, string>> = {};
  for (const key of PLACEMENT_SIZE_KEYS) {
    const entry = parseRememberedSize(key, source[key]);
    if (entry === undefined) continue;
    sizes[key] = entry;
    // 書き込み ID は、有効なサイズを持つ種類のものだけを読む。
    const writeId = writeIdSource[key];
    if (isValidWriteId(writeId)) sizeWriteIds[key] = writeId;
  }
  return {
    lastPlacedKind: isPlaceableObjectKind(value.lastPlacedKind) ? value.lastPlacedKind : undefined,
    lastShapeKind: isShapeKind(value.lastShapeKind) ? value.lastShapeKind : undefined,
    sizes,
    sizeWriteIds,
  };
}

function isValidWriteId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= WRITE_ID_MAX_LENGTH;
}

function parseRememberedSize(
  key: PlacementSizeKey,
  value: unknown
): RememberedPlacementSize | undefined {
  if (!isRecord(value)) return undefined;
  const limits = placementSizeLimitsOf(key);
  const { w, h } = value;
  if (!isValidDimension(w, limits.maxW)) return undefined;
  if (h === undefined) return key === "text" ? { w } : undefined;
  if (!isValidDimension(h, limits.maxH)) return undefined;
  return { w, h };
}

function isValidDimension(value: unknown, max: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 && value <= max;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isShapeKind(value: unknown): value is ShapeKind {
  return value === "rectangle" || value === "circle" || value === "arrow";
}

function sameRememberedSize(
  a: RememberedPlacementSize | undefined,
  b: RememberedPlacementSize | undefined
): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.w === b.w && a.h === b.h;
}

/**
 * 他タブの保存を取り込む。値か書き込み ID が変わった種類は他タブの値を最新とし、その種類の未確定の
 * 記録を捨てる（先に送った自分の op が後から拒否されても、他タブの新しい値を上書きしないため）。
 * 寸法と書き込み ID の両方が同じときだけ変わっていないとみなす。寸法が同じでも書き込み ID が違えば、
 * 他タブが同じ寸法で書き直した記録として扱う。書き込み ID の無い保存値は、自分の記録かどうかを
 * 見分けられないため、同じく他タブの記録として扱う（拒否で他タブの値を消すより、拒否された値を残す方を選ぶ）。
 * 前回の種別・図形の種類も、再読み込みしたときと同じ結果になるよう保存値に合わせる（キーの削除や
 * 項目の欠落では図形の種類を消し、種別は初回の読み込みと同じく旧キーの種別へ戻す）。
 */
function importExternalPreference(state: MemoryState, parsed: ParsedPreference): void {
  for (const key of PLACEMENT_SIZE_KEYS) {
    const incoming = parsed.sizes[key];
    const incomingWriteId = parsed.sizeWriteIds[key];
    if (
      sameRememberedSize(state.sizes[key], incoming) &&
      incomingWriteId === state.sizeWriteIds[key]
    ) {
      continue;
    }
    state.sizes = withSize(state.sizes, key, incoming);
    state.sizeWriteIds = withSizeWriteId(state.sizeWriteIds, key, incomingWriteId);
    pendingWrites.delete(key);
  }
  state.lastPlacedKind = parsed.lastPlacedKind ?? readStoredLastPlacedObjectKind() ?? undefined;
  state.lastShapeKind = parsed.lastShapeKind;
}

function installStorageListener(): void {
  if (storageListenerInstalled || typeof window === "undefined") return;
  storageListenerInstalled = true;
  window.addEventListener("storage", (event: StorageEvent) => {
    // 全消去（key === null）を含め、このキーの変更だけを扱う。値はイベントではなく localStorage から
    // 読み直す（sessionStorage の同名キーの変更を取り違えないため）。
    if (event.key !== null && event.key !== KEY) return;
    const state = memory;
    if (state === null) return;
    syncExternalChanges(state);
  });
}
